import {
  SortEnum,
  VisitSortByEnum,
  type PackageDepartmentEnum,
} from "@/__generated__/graphql";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { VisitCard } from "@/components/shared/visits/visit-card";
import { Button, Skeleton, View } from "@/components/ui";
import { getServiceNameFromDepartment } from "@/lib/booking-lib";
import { useVisitDetailStore } from "@/store/visits/visitDetail";
import { useVisitStore } from "@/store/visits/visitStore";
import { useRoute } from "@/components/shared/router";
import { bookingRoutes } from "@/components/hooks/use-booking-route";
import {
  useVisitIssueStore,
  VisitIssueType,
} from "@/store/report-issue/visit-issue/visitIssue";
import { useEffect } from "react";
import { ModalHeader } from "./modal-header";
import { useGetVisitsQuery } from "@/gqlhooks/visit/useGetVisitsQuery";
import { useAuthState } from "@/store/auth";

export type RelatedVisitPackageModalProps = {
  open: boolean;
  packageId: string;
  handleClose(): void;
};

export const RelatedVisitPackageModal = ({
  open,
  packageId,
  handleClose,
}: RelatedVisitPackageModalProps) => {
  const { push } = useRoute();

  const clientId = useAuthState((state) => state.data.userInfo.id);

  const { list, loading, canLoadMore, variables, fetchMore } =
    useGetVisitsQuery({
      variables: {
        filters: {
          packageId: [packageId],
          clientId: [clientId],
          limit: 5,
          offset: 0,
          sortBy: [VisitSortByEnum.ServiceDate],
          sort: [SortEnum.Asc],
        },
      },
    });

  const onLoadMore = async () => {
    if (!canLoadMore || loading) return;
    const filters = {
      ...variables?.filters,
      offset: list.length,
    };
    await fetchMore({
      variables: {
        filters,
      },
    });
  };

  const visitIssue = useVisitIssueStore(
    ({ data }) => data.showModal && data.issueType,
  );
  const {
    openRateVisitModalById,
    openRescheduleVisitConfirmation,
    openVisitModal,
  } = useVisitDetailStore();

  const openVisitIssueModal = useVisitIssueStore(
    (state) => state.openVisitIssueModal,
  );

  const handleRebook = (department: PackageDepartmentEnum) => {
    const serviceName = getServiceNameFromDepartment(department);
    push(bookingRoutes[serviceName][0]);
  };

  const handleRateVisit = (visitId: string, rate?: number) => {
    openRateVisitModalById(visitId, rate);
    useVisitStore.getState().fetch({
      requestPayload: { id: visitId },
    });
  };

  const handleSetSchedule = (visitId: string) => {
    setTimeout(() => {
      handleClose();
      openRescheduleVisitConfirmation(visitId);
    }, 200);
  };

  useEffect(() => {
    if (visitIssue) {
      push({
        pageKey: "reportIssueVisit",
        params: {
          type: visitIssue,
        },
      });
    }
  }, [visitIssue]);

  const handleReportVisit = (visitId: string) => {
    handleClose();
    openVisitIssueModal(VisitIssueType.reportVisit, visitId, false);
  };

  const handleViewDetail = (visitId: string) => {
    handleClose();
    setTimeout(() => {
      openVisitModal(visitId);
    }, 500);
  };

  return (
    <BottomDrawerModal open={open} onOpenChange={handleClose}>
      <ModalHeader onClose={handleClose} />

      {loading && list.length === 0 ? (
        <View className="flex flex-col gap-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </View>
      ) : (
        <View className="flex flex-col gap-2">
          {list.map((visit) => (
            <VisitCard
              key={visit.id}
              visitData={visit}
              handleViewDetail={() => handleViewDetail(visit.id)}
              handleRateVisit={handleRateVisit}
              handleRebook={handleRebook}
              handleSetSchedule={handleSetSchedule}
              handleReportVisit={handleReportVisit}
            />
          ))}
        </View>
      )}

      {canLoadMore && (
        <Button
          variant="tertiary"
          color="CTA2"
          iconColor="CTA2"
          className="w-full"
          loading={loading}
          disabled={loading}
          onClick={onLoadMore}
          iconName="arrowCounterClockwise"
          iconAlignment="end"
          children="Show More"
        />
      )}
    </BottomDrawerModal>
  );
};
